import { useRef, useState } from 'react'
import { Upload, Loader2, X } from 'lucide-react'
import { adminApi } from '../../lib/api'
import type { UploadedFile } from '../../types'

export interface SingleFileUploaderProps {
  label?: string
  helperText?: string
  value?: UploadedFile | null
  onChange: (file: UploadedFile | null) => void
  tripId?: string
  eventId?: string
  userId?: string // Waybook user ID to link the file to
  privacy?: 'public' | 'trip_members' | 'event_members' | 'private' | 'custom'
  accept?: string
  disabled?: boolean
}

export function SingleFileUploader({
  label,
  helperText,
  value = null,
  onChange,
  tripId,
  eventId,
  userId,
  privacy = 'public',
  accept = 'image/*',
  disabled = false,
}: SingleFileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileSelected = async (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return
    const file = fileList[0]

    setUploading(true)
    setError(null)
    try {
      const response = await adminApi.uploadFile(file, {
        trip_id: tripId,
        event_id: eventId,
        user_id: userId,  // Link file to this waybook user
        privacy,
      })

      const payload = response.data?.data || response.data
      if (!payload) {
        throw new Error('Empty response from upload endpoint')
      }

      const uploadedFile: UploadedFile = {
        id: payload.id,
        url: payload.url || payload.file_url,
        thumbnailUrl: payload.thumbnail_url,
        fileType: payload.file_type || file.type,
        size: payload.file_size || file.size,
        width: payload.width,
        height: payload.height,
        filename: payload.filename || payload.name || file.name,
        originalFilename: payload.original_filename || file.name,
        filePath: payload.file_path,
        privacy: payload.privacy,
        description: payload.description,
        tags: payload.tags,
      }

      onChange(uploadedFile)
    } catch (err: any) {
      console.error('SingleFileUploader upload failed:', err)
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        headers: err.response?.headers,
      })
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload file'
      setError(`Upload failed: ${errorMsg}`)
    } finally {
      setUploading(false)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    }
  }

  const handleClick = () => {
    if (disabled || uploading) return
    inputRef.current?.click()
  }

  const removeFile = () => {
    if (uploading || disabled) return
    onChange(null)
  }

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {helperText && (
        <p className="text-xs text-gray-500">{helperText}</p>
      )}

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        onChange={(event) => handleFileSelected(event.target.files)}
      />

      {value ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="relative bg-gray-100">
            {value.url ? (
              <img
                src={value.thumbnailUrl || value.url}
                alt={value.filename || 'Uploaded file'}
                className="w-full max-h-64 object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                No preview available
              </div>
            )}
            <button
              type="button"
              onClick={removeFile}
              disabled={uploading || disabled}
              className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full text-gray-600 shadow"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-3 text-sm text-gray-600">
            <div className="font-medium text-gray-900">{value.filename || value.originalFilename}</div>
            <div className="text-xs text-gray-500 mt-1">
              {value.fileType || 'Unknown type'}
              {value.size ? ` • ${(value.size / (1024 * 1024)).toFixed(2)} MB` : ''}
            </div>
            <button
              type="button"
              onClick={handleClick}
              disabled={uploading || disabled}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Replace image
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          disabled={uploading || disabled}
          className="w-full p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex flex-col items-center gap-2">
            {uploading ? (
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            ) : (
              <Upload className="w-8 h-8" />
            )}
            <span className="text-sm font-medium">
              {uploading ? 'Uploading...' : 'Click to upload'}
            </span>
            <span className="text-xs">
              {accept === 'image/*' ? 'PNG, JPG, WEBP, GIF up to ~20MB' : accept}
            </span>
          </div>
        </button>
      )}

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
    </div>
  )
}

export default SingleFileUploader
